import type { ReactNode } from 'react';
import { PolyIcon } from '@/components/icons/skills/poly-icon';
import { BrandLogo } from '@/components/icons/brand-logo';
import { Globe, FileText, Database, Image } from 'lucide-react';

// Use case type definition - single source of truth
export interface UseCase {
    id: string;
    createdAt: Date;
    videoPath: string;
    posterPath?: string;
    title: string;
    subtitle: string;
    sections: {
        title: string;
        items: string[];
    }[];
    techStack: {
        icon: ReactNode;
        name: string;
    }[];
    detailPath: string;
}

// Icon size class for consistency
const ICON_CLASS = "[@media(min-width:1390px)]:w-12 [@media(min-width:1390px)]:h-12 w-10 h-10";

/**
 * USE CASES REGISTRY
 * 
 * Add new use cases at the TOP of this array (newest first).
 * The scroll transition will automatically handle however many use cases exist.
 * 
 * To add a new use case:
 * 1. Convert your .mov to .mp4 using: 
 *    ffmpeg -i "input.mov" -c:v libx264 -preset slow -crf 23 -c:a aac -b:a 128k -movflags +faststart "public/videos/your-video.mp4"
 * 2. Add a new entry at the TOP of this array
 * 3. The UI will automatically include it in the carousel
 */
export const useCasesRegistry: UseCase[] = [
    // ========== NEWEST FIRST ==========

    {
        id: 'ad-revenue-loop',
        createdAt: new Date('2026-01-23'),
        videoPath: '/videos/ad-revenue-loop.mp4',
        title: 'Autonomous Revenue Loop',
        subtitle: 'From Idea to Active Ad Spend in 6 Minutes',
        sections: [
            {
                title: 'Problem',
                items: [
                    '• Great ideas die because going from concept to live ad requires 3 tools and 2 people',
                    '• Manually resizing, reformatting, and uploading variations is a low-leverage time sink'
                ]
            },
            {
                title: 'Solution',
                items: [
                    '• Unified Agent Workforce: Messaging Strategist, Image Designer, and Meta Worker in one thread',
                    '• Parallel Execution: Builds campaign, sets budget, launches multiple creatives simultaneously'
                ]
            },
            {
                title: 'Impact',
                items: [
                    '• Zero-Touch Launch: Raw conversation to ACTIVE campaign in under 6 minutes',
                    '• Multiple creative angles (Pain-First vs. Corporate) deployed in parallel'
                ]
            }
        ],
        techStack: [
            { icon: <BrandLogo name="meta" className={ICON_CLASS} />, name: 'Meta Ads' },
            { icon: <BrandLogo name="openai" className={ICON_CLASS} />, name: 'GPT Image' },
            { icon: <PolyIcon className={ICON_CLASS} />, name: 'Poly' }
        ],
        detailPath: '/case-studies/ad-revenue-loop'
    },

    {
        id: 'browser-automation',
        createdAt: new Date('2026-01-23'),
        videoPath: '/videos/browser-automation.mp4',
        title: 'Context-Aware Site Audits',
        subtitle: 'Turning Transcripts into QA',
        sections: [
            {
                title: 'Problem',
                items: [
                    '• Manually checking hundreds of pages for forbidden words is slow and error-prone',
                    '• Client instructions get lost in meeting transcripts instead of being acted upon'
                ]
            },
            {
                title: 'Solution',
                items: [
                    '• Transcript-to-Action Pipeline: Drop transcript, extract constraints automatically',
                    '• Intelligent Browser Worker: Crawls live site, scans pages, maps every violation'
                ]
            },
            {
                title: 'Impact',
                items: [
                    '• 100% Accuracy: Identified every compliance risk without human intervention',
                    '• Immediate ROI: Converted unbillable admin work into digital labor'
                ]
            }
        ],
        techStack: [
            { icon: <Globe className={ICON_CLASS} />, name: 'Browser Worker' },
            { icon: <FileText className={ICON_CLASS} />, name: 'Meeting Transcripts' },
            { icon: <PolyIcon className={ICON_CLASS} />, name: 'Poly' },
            { icon: <BrandLogo name="openai" className={ICON_CLASS} />, name: 'LLM' }
        ],
        detailPath: '/case-studies/browser-automation'
    },

    {
        id: 'generative-ui',
        createdAt: new Date('2026-01-23'),
        videoPath: '/videos/generative-ui.mp4',
        title: 'Generative UI Prototyping',
        subtitle: 'Data-Driven Design Systems',
        sections: [
            {
                title: 'Problem',
                items: [
                    '• Turning complex backend data models into frontend UI requires weeks of wireframing',
                    '• Standard image generators don\'t understand your business logic or brand identity'
                ]
            },
            {
                title: 'Solution',
                items: [
                    '• Deep Context Retrieval: Searches Vector Memory for data relationships and brand identity',
                    '• Multi-View Generation: Prototypes 3 distinct UI approaches based on actual data structure'
                ]
            },
            {
                title: 'Impact',
                items: [
                    '• Rapid Iteration: Abstract data model to high-fidelity prototype in minutes',
                    '• Brand Consistency: All outputs respect pre-defined visual style without constant prompting'
                ]
            }
        ],
        techStack: [
            { icon: <Database className={ICON_CLASS} />, name: 'Vector Memory' },
            { icon: <Image className={ICON_CLASS} />, name: 'Image Gen' },
            { icon: <PolyIcon className={ICON_CLASS} />, name: 'Poly' },
            { icon: <BrandLogo name="openai" className={ICON_CLASS} />, name: 'LLM' }
        ],
        detailPath: '/case-studies/generative-ui'
    }

    // ========== OLDEST LAST ==========
];

// Helper to get use case by index
export function getUseCaseByIndex(index: number): UseCase | undefined {
    return useCasesRegistry[index];
}

// Helper to get use case by id
export function getUseCaseById(id: string): UseCase | undefined {
    return useCasesRegistry.find(uc => uc.id === id);
}

// Get total count
export function getUseCasesCount(): number {
    return useCasesRegistry.length;
}
